import {CustomError} from '../../../../libs/shared/helpers/src/types/customErrors';
import {ActionBase, ActionWithPayload} from '../../../helpers/reduxHelpers';
import {AnswerModel, ApiAnswer} from '../../../models/api/answerModel';
import {ApiAnswers} from '../../../models/api/answersModel';
import {fetchApiAsync, postApiAsync} from '../../../services/http/http';
import {HttpResponseError} from '../../../services/http/httpError';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {fetchAllAnswerFoldersAction} from '../answerFolders/answerFolderActions';
import {getCompany} from '../company/companySelectors';

/*
 * Action enum.
 */

export enum AnswerActionsEnum {
  ANSWER_UPDATED = '@@data/answers/ANSWER_UPDATED',
  ANSWER_DELETED = '@@data/answers/ANSWER_DELETED',
  ANSWERS_DELETED = '@@data/answers/ANSWERS_DELETED',
  FETCH_ANSWERS = '@@data/answers/FETCH_ANSWERS',
  FETCH_ANSWERS_COMPLETED = '@@data/answers/FETCH_ANSWERS_COMPLETED',
  FETCH_ANSWERS_FAILED = '@@data/answers/FETCH_ANSWERS_FAILED',
  ANSWERS_MARK_AS_STALE = '@@data/answers/ANSWERS_MARK_AS_STALE',
}

/*
 * Action Types.
 */

export interface AnswerUpdatedAction extends ActionWithPayload<AnswerModel> {
  type: AnswerActionsEnum.ANSWER_UPDATED;
}

export interface AnswerDeletedAction extends ActionWithPayload<number> {
  type: AnswerActionsEnum.ANSWER_DELETED;
}

export interface AnswersDeletedAction extends ActionWithPayload<ReadonlyArray<number>> {
  type: AnswerActionsEnum.ANSWERS_DELETED;
}

export interface FetchAnswersAction extends ActionBase {
  type: AnswerActionsEnum.FETCH_ANSWERS;
}

export interface FetchAnswersCompletedAction extends ActionWithPayload<ReadonlyArray<AnswerModel>> {
  type: AnswerActionsEnum.FETCH_ANSWERS_COMPLETED;
}

export interface FetchAnswersFailedAction extends ActionWithPayload<string> {
  type: AnswerActionsEnum.FETCH_ANSWERS_FAILED;
}

export interface AnswersMarkAsStaleAction extends ActionBase {
  type: AnswerActionsEnum.ANSWERS_MARK_AS_STALE;
}

export class AnswerNameAlreadyExistsError extends CustomError {}

/*
 * Action creators.
 */

export function createAnswerAsyncAction(
  namespace: string,
  name: string,
  folderId: number | undefined,
  body: string,
): ThunkAsyncAction<AnswerModel> {
  return async (dispatch, getState) => {
    const state = getState();
    const company = getCompany(state);

    const url = `${company.url}/canned_answers`;
    const requestBody = {
      namespace,
      name,
      body,
      ...(folderId && {
        folder: {
          id: folderId,
        },
      }),
    };

    try {
      const answer = await postApiAsync(ApiAnswer, url, requestBody);
      dispatch(updateAnswerAction(answer));

      return answer;
    } catch (error) {
      if (!(error instanceof HttpResponseError)) {
        throw error;
      }

      switch (error.status) {
        case 409:
          throw new AnswerNameAlreadyExistsError();
        default:
          throw error;
      }
    }
  };
}

export function updateAnswerFromPushAsyncAction(answerId: number): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const company = getCompany(state);

    try {
      const url = `${company.url}/canned_answers/${answerId}`;
      const answer = await fetchApiAsync(ApiAnswer, url);

      dispatch(updateAnswerAction(answer));
    } catch (error) {
      // We get notified for canned answers belonging to other teams, and a 403 is expected in that case.
      if (error instanceof HttpResponseError && error.status === 403) {
        return;
      }

      throw error;
    }
  };
}

function updateAnswerAction(answer: AnswerModel): AnswerUpdatedAction {
  return {
    payload: answer,
    type: AnswerActionsEnum.ANSWER_UPDATED,
  };
}

export function fetchAllAnswersAction(): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const company = getCompany(state);

    dispatch(fetchAnswersAction());

    try {
      const url = `${company.url}/canned_answers`;
      const answers = await fetchApiAsync(ApiAnswers, url);
      dispatch(fetchAnswersCompletedAction(answers.cannedAnswers));
    } catch (error) {
      const errorMessage = error instanceof HttpResponseError ? error.reason : undefined;
      dispatch(fetchAnswersFailedAction(errorMessage));
    }
  };
}

export function fetchAnswersAndFoldersAction(): ThunkAsyncAction {
  return async (dispatch) => {
    dispatch(fetchAllAnswersAction());
    dispatch(fetchAllAnswerFoldersAction());
  };
}

export function answerDeletedAction(answerId: number): ThunkAction {
  return (dispatch, getState) => {
    const existingAnswer = getState().data.answers.byId[answerId];

    if (!existingAnswer) {
      return;
    }

    dispatch<AnswerDeletedAction>({
      payload: answerId,
      type: AnswerActionsEnum.ANSWER_DELETED,
    });
  };
}

export function markAnswersAsStaleAction(): AnswersMarkAsStaleAction {
  return {
    type: AnswerActionsEnum.ANSWERS_MARK_AS_STALE,
  };
}

function fetchAnswersAction(): FetchAnswersAction {
  return {
    type: AnswerActionsEnum.FETCH_ANSWERS,
  };
}

function fetchAnswersCompletedAction(answers: ReadonlyArray<AnswerModel>): FetchAnswersCompletedAction {
  return {
    type: AnswerActionsEnum.FETCH_ANSWERS_COMPLETED,
    payload: answers,
  };
}

function fetchAnswersFailedAction(error?: string): FetchAnswersFailedAction {
  return {
    type: AnswerActionsEnum.FETCH_ANSWERS_FAILED,
    payload: error || 'Failed to fetch answers.',
  };
}

/*
 * Union type.
 */

export type AnswerAction =
  | AnswerUpdatedAction
  | AnswerDeletedAction
  | AnswersDeletedAction
  | FetchAnswersAction
  | FetchAnswersCompletedAction
  | FetchAnswersFailedAction
  | AnswersMarkAsStaleAction;
